import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import localStore from '@shared/utils/localStore.ts';
import { HotelUserTokenDto } from '@store/services/hotelAuthApi';
import { HotelUserDto } from '@store/services/hotelUserApi';

interface AuthState {
  tokens?: { accessToken: string | null; refreshToken: string | null };
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: HotelUserDto;
  permissions?: string[];
  permissionsReady: boolean;
}

// Define root state interface for this slice
interface RootStateWithAuth {
  auth: AuthState;
  [key: string]: unknown; // Allow for other slices
}

const initialState: AuthState = {
  tokens: localStore.getTokens(),
  isAuthenticated: localStore.getTokens().accessToken !== null,
  isLoading: false,
  user: undefined,
  permissions: [],
  permissionsReady: false,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<HotelUserTokenDto>) => {
      const {
        tokens: { accessToken, refreshToken },
      } = action.payload;
      localStore.setTokens({ accessToken, refreshToken });
      state.tokens = { accessToken, refreshToken };
      state.isAuthenticated = true;
      state.isLoading = false;
      state.user = undefined;
      state.permissions = [];
      state.permissionsReady = false;
    },
    logout: state => {
      state.tokens = undefined;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.permissions = [];
      state.permissionsReady = false;
      state.user = undefined;

      localStore.clearTokens();
      localStore.deleteUserId();
    },
    getUser: (state, action: PayloadAction<HotelUserDto>) => {
      state.user = action.payload;
      state.isLoading = false;
    },
    loading: state => {
      state.isLoading = true;
    },
    updateTokens: (state, action: PayloadAction<HotelUserTokenDto>) => {
      const {
        tokens: { accessToken, refreshToken },
      } = action.payload;
      localStore.setTokens({ accessToken, refreshToken });
      state.tokens = { accessToken, refreshToken };
    },
    setPermissions: (state, action: PayloadAction<string[]>) => {
      state.permissions = action.payload;
      state.permissionsReady = true;
    },
  },
});

export const { login, logout, getUser, loading, updateTokens, setPermissions } = authSlice.actions;

export const selectAuthUser = (state: RootStateWithAuth) => state.auth.user;

export const selectAuthUserLanguage = () => {
  return localStore.getLanguage() || 'cn';
};

export const selectIsAuthenticated = (state: RootStateWithAuth) => state.auth.isAuthenticated;

export const selectPermissions = (state: RootStateWithAuth) => state.auth.permissions || [];

export const selectPermissionsReady = (state: RootStateWithAuth) => state.auth.permissionsReady;

export const selectUserId = (state: RootStateWithAuth) => {
  return state.auth.user?.id || undefined;
};

export default authSlice.reducer;
